import type {ReactElement} from 'react';
import React from 'react';
import {Text, TextSize} from '@Components/text';
import {Checkbox, CHECKBOX_TYPE} from '@Components/checkbox';
import {getAssetUrl} from '@Utils/s3.util';
import styles from './page-select-list.module.scss';

const PLACEHOLDER_THUMBNAIL = 'placeholder-image.png';

interface PageSelectListProps {
  pageOrder: string[];
  selectedPages: string[];
  onSelectedPagesChange: (pages: string[]) => void;
  thumbnailsArray: string[];
  selectAllLabel?: string;
}

export function PageSelectList({pageOrder, selectedPages, onSelectedPagesChange, thumbnailsArray, selectAllLabel}: PageSelectListProps): ReactElement {
  const totalNumberOfPages = pageOrder.length;

  const handleSelectAll = (): void => {
    if (selectedPages.length === totalNumberOfPages) {
      onSelectedPagesChange([pageOrder[0]]);
    } else {
      onSelectedPagesChange([...pageOrder]);
    }
  };

  const handlePageToggle = (pageId: string): void => {
    if (selectedPages.includes(pageId)) {
      onSelectedPagesChange(selectedPages.filter((page) => page !== pageId));
    } else {
      const next = [...selectedPages, pageId];
      next.sort((a, b) => pageOrder.indexOf(a) - pageOrder.indexOf(b));
      onSelectedPagesChange(next);
    }
  };

  const getThumbnail = (src: string): ReactElement => {
    const effectiveSrc = src || getAssetUrl(PLACEHOLDER_THUMBNAIL);
    return (
      <div className={`${styles.thumbnailWrapper} radius-4 border-s-standard`}>
        <img
          src={effectiveSrc}
          alt=""
          className={`${styles.img} _full-width _full-height`}
          onError={(e): void => {
            (e.target as HTMLImageElement).src = getAssetUrl(PLACEHOLDER_THUMBNAIL);
          }}
        />
      </div>
    );
  };

  return (
    <ul className="flex-v-row _full-width spacing-m-0">
      <li
        className={`flex-h-row ${styles.selectAllListItem} radius-8 hover-transition-all flex-items-center spacing-m-b-2 spacing-p-t-1 spacing-p-b-1 spacing-p-r-3 spacing-p-l-3 ${styles.listItem} _cursor-pointer`}
        key="select-all"
        onClick={handleSelectAll}
      >
        <Checkbox isChecked={selectedPages.length === totalNumberOfPages} className="spacing-m-r-3" type={CHECKBOX_TYPE.PRIMARY} />
        <Text className="ct-neutral-primary" bold val={selectAllLabel ?? window.i18next.t('pmwjs_select_all_pages', {pages: totalNumberOfPages})} size={TextSize.XSMALL} />
      </li>
      {pageOrder.map((page, index) => {
        const pageNumber = index + 1;
        const isPageSelected = selectedPages.includes(page);

        return (
          <li
            className={`flex-h-row ct-neutral-primary radius-8 hover-transition-all flex-items-center spacing-m-b-2 spacing-p-t-1 spacing-p-b-1 spacing-p-r-3 spacing-p-l-3 ${styles.listItem} _cursor-pointer`}
            key={page}
            onClick={(): void => {
              handlePageToggle(page);
            }}
          >
            <Checkbox isChecked={isPageSelected} className="spacing-m-r-3" type={CHECKBOX_TYPE.PRIMARY} />
            {getThumbnail(thumbnailsArray[index] ?? '')}
            <Text bold val={`${window.i18next.t('pmwjs_page')} ${pageNumber}`} className="spacing-m-l-3" size={TextSize.XSMALL} />
          </li>
        );
      })}
    </ul>
  );
}
